import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

export type UserProfile = {
  displayName: string;
  email: string;
  photoURL?: string;
  createdAt: Date;
  updatedAt: Date;
};

export type AuthContextType = {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isNewUser: boolean;
  setIsNewUser: (v: boolean) => void;
  signup: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<User | void>;
  logout: () => Promise<void>;
  updateUserDisplayName: (displayName: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

// Detect if running inside a WebView (mobile app)
const isWebView = (): boolean => {
  const ua = navigator.userAgent;
  return (
    /wv/.test(ua) ||
    /Android.*Version\/[\d.]+.*Chrome\/[\d.]+ Mobile Safari/.test(ua)
  );
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('🔍 Fetching user profile for:', userId);
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('📄 Firestore user data:', data);
        const profile = {
          displayName: data.displayName || '',
          email: data.email || '',
          photoURL: data.photoURL,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
        console.log('✅ Setting userProfile to:', profile);
        setUserProfile(profile);
      } else {
        console.log('❌ User document does not exist in Firestore');
        setUserProfile(null);
      }
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      setUserProfile(null);
    }
  };

  const handleGoogleUser = async (googleUser: User) => {
    const userDoc = await getDoc(doc(db, 'users', googleUser.uid));
    if (!userDoc.exists()) {
      await setDoc(doc(db, 'users', googleUser.uid), {
        email: googleUser.email,
        displayName: '',
        photoURL: googleUser.photoURL || '',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
    await fetchUserProfile(googleUser.uid);
  };

  useEffect(() => {
    // Handle redirect result when app loads (for WebView Google Sign-In)
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          console.log('✅ Google redirect sign-in successful');
          await handleGoogleUser(result.user);
        }
      })
      .catch((error) => {
        console.error('❌ Redirect result error:', error);
      });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email: string, password: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', result.user.uid), {
      email,
      displayName: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    await fetchUserProfile(result.user.uid);
    setIsNewUser(true);
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async (): Promise<User | void> => {
    const provider = new GoogleAuthProvider();

    if (isWebView()) {
      // Redirect flow for WebView — page will reload and getRedirectResult handles it
      await signInWithRedirect(auth, provider);
      return; // Function ends here, result is handled in useEffect above
    } else {
      // Popup flow for normal browser
      const result = await signInWithPopup(auth, provider);
      await handleGoogleUser(result.user);
      return result.user;
    }
  };

  const updateUserDisplayName = async (displayName: string) => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      const trimmedName = displayName.trim();

      if (!trimmedName) throw new Error('Display name cannot be empty');
      if (trimmedName.length < 2) throw new Error('Display name must be at least 2 characters');
      if (trimmedName.length > 50) throw new Error('Display name must be less than 50 characters');

      await updateProfile(user, { displayName: trimmedName });

      await setDoc(
        doc(db, 'users', user.uid),
        { displayName: trimmedName, updatedAt: new Date() },
        { merge: true }
      );

      setUserProfile(prev =>
        prev
          ? { ...prev, displayName: trimmedName, updatedAt: new Date() }
          : {
              displayName: trimmedName,
              email: user.email || '',
              createdAt: new Date(),
              updatedAt: new Date(),
            }
      );

      await fetchUserProfile(user.uid);
      console.log('✅ Display name updated successfully:', trimmedName);
    } catch (error) {
      console.error('❌ Error updating display name:', error);
      throw error;
    }
  };

  const logout = () => signOut(auth);

  const value = {
    user,
    userProfile,
    loading,
    isNewUser,
    setIsNewUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    updateUserDisplayName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}